import * as THREE from "three";
import type { SuntraceColorGrade } from "./colorScript";

export const SUNTRACE_SURFACE_PALETTE = {
  asphalt: 0x32363a,
  shoulder: 0x343936,
  limestone: 0xd4c5aa,
  limestoneDark: 0xafa38e,
  terrain: 0xb0a58c,
  safety: 0xb74732,
  roadMarking: 0xe8dfca,
  puddle: 0x536a70,
  guardrail: 0xaab0ac,
  guardrailInner: 0x56615f,
  rock: 0x968873,
  vegetation: 0x304332,
  waterDeep: 0x0d222d,
  waterSurface: 0x164354,
  waterReflection: 0x2c6172,
} as const;

export const SUNTRACE_SURFACE_TINT = {
  limestoneDark: 0xd5d5d5,
  rock: 0xd5d5d5,
} as const;

const getHexStyle = (color: number): string =>
  `#${color.toString(16).padStart(6, "0")}`;

const getColorChannels = (
  color: number,
): { red: number; green: number; blue: number } => ({
  red: color >> 16 & 0xff,
  green: color >> 8 & 0xff,
  blue: color & 0xff,
});

const requireContext = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("SUNTRACE requires a 2D canvas context for procedural materials");
  }
  return context;
};

const createCanvas = (width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const createRandom = (seed: number): (() => number) => {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4_294_967_296;
  };
};

export const SUNTRACE_ASPHALT_REPAIR_COLOR = {
  red: 20,
  green: 22,
  blue: 23,
} as const;

export type SuntraceAsphaltRepairSegment = {
  repairIndex: number;
  startX: number;
  endX: number;
  startY: number;
  controlY1: number;
  controlY2: number;
  endY: number;
  lineWidth: number;
  opacity: number;
};

export const createSuntraceAsphaltRepairProfile =
  (): SuntraceAsphaltRepairSegment[] => {
    const textureSize = 1_024;
    const edgeMargin = 48;
    const random = createRandom(0x7a11ce55);
    const segments: SuntraceAsphaltRepairSegment[] = [];

    for (let repairIndex = 0; repairIndex < 7; repairIndex += 1) {
      const repairSpan = 220 + random() * 320;
      const repairStart =
        edgeMargin + random() * (textureSize - repairSpan - edgeMargin * 2);
      const segmentCount = 2 + Math.floor(random() * 3);
      const gap = 14 + random() * 20;
      const segmentSpan =
        (repairSpan - gap * (segmentCount - 1)) / segmentCount;
      const baseY = 56 + random() * 912;
      const verticalDrift = (random() - 0.5) * 22;

      for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex += 1) {
        const startX =
          repairStart + segmentIndex * (segmentSpan + gap);
        const endX = startX + segmentSpan * (0.78 + random() * 0.22);
        const startProgress = (startX - repairStart) / repairSpan;
        const endProgress = (endX - repairStart) / repairSpan;
        const startY =
          baseY + (startProgress - 0.5) * verticalDrift + (random() - 0.5) * 5;
        const endY =
          baseY + (endProgress - 0.5) * verticalDrift + (random() - 0.5) * 5;

        segments.push({
          repairIndex,
          startX,
          endX,
          startY,
          controlY1: startY + (random() - 0.5) * 9,
          controlY2: endY + (random() - 0.5) * 9,
          endY,
          lineWidth: 1.25 + random() * 1.75,
          opacity: 0.032 + random() * 0.036,
        });
      }
    }

    return segments;
  };

export const SUNTRACE_LIMESTONE_WEATHERING_RESPONSE = Object.freeze({
  stainCount: 18,
  minOpacity: 0.035,
  maxOpacity: 0.085,
  maxWidth: 220,
  maxHeight: 120,
});

export type SuntraceLimestoneWeatheringStain = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
};

export const createSuntraceLimestoneWeatheringProfile =
  (): SuntraceLimestoneWeatheringStain[] => {
    const random = createRandom(0x6e0f4a2d);
    return Array.from(
      { length: SUNTRACE_LIMESTONE_WEATHERING_RESPONSE.stainCount },
      () => ({
        x: random() * 512,
        y: random() * 512,
        width: 86 + random() * 134,
        height: 28 + random() * 92,
        rotation: (random() - 0.5) * 0.62,
        opacity:
          SUNTRACE_LIMESTONE_WEATHERING_RESPONSE.minOpacity +
          random() *
            (SUNTRACE_LIMESTONE_WEATHERING_RESPONSE.maxOpacity -
              SUNTRACE_LIMESTONE_WEATHERING_RESPONSE.minOpacity),
      }),
    );
  };

type SurfaceTextureSet = {
  color: THREE.CanvasTexture;
  bump: THREE.CanvasTexture;
  roughness: THREE.CanvasTexture;
};

const configureSurfaceTexture = (
  canvas: HTMLCanvasElement,
  anisotropy: number,
  colorSpace: THREE.ColorSpace,
  repeatX: number,
  repeatY: number,
): THREE.CanvasTexture => {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = colorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  texture.anisotropy = anisotropy;
  return texture;
};

const createAsphaltTextures = (anisotropy: number): SurfaceTextureSet => {
  const colorCanvas = createCanvas(1024, 1024);
  const colorContext = requireContext(colorCanvas);
  const bumpCanvas = createCanvas(1024, 1024);
  const bumpContext = requireContext(bumpCanvas);
  const roughnessCanvas = createCanvas(1024, 1024);
  const roughnessContext = requireContext(roughnessCanvas);
  const colorImage = colorContext.createImageData(1024, 1024);
  const bumpImage = bumpContext.createImageData(1024, 1024);
  const roughnessImage = roughnessContext.createImageData(1024, 1024);
  const random = createRandom(0x51a7face);
  const baseColor = getColorChannels(SUNTRACE_SURFACE_PALETTE.asphalt);

  for (let pixel = 0; pixel < 1024 * 1024; pixel += 1) {
    const grain = random() * 0.72 + random() * 0.28;
    const variation = Math.round((grain - 0.5) * 24);
    const offset = pixel * 4;
    colorImage.data[offset] = THREE.MathUtils.clamp(
      baseColor.red + variation,
      0,
      255,
    );
    colorImage.data[offset + 1] = THREE.MathUtils.clamp(
      baseColor.green + variation,
      0,
      255,
    );
    colorImage.data[offset + 2] = THREE.MathUtils.clamp(
      baseColor.blue + variation,
      0,
      255,
    );
    colorImage.data[offset + 3] = 255;
    const aggregate = Math.round(72 + grain * 112);
    bumpImage.data[offset] = aggregate;
    bumpImage.data[offset + 1] = aggregate;
    bumpImage.data[offset + 2] = aggregate;
    bumpImage.data[offset + 3] = 255;
    const roughness = Math.round(188 + random() * 48);
    roughnessImage.data[offset] = roughness;
    roughnessImage.data[offset + 1] = roughness;
    roughnessImage.data[offset + 2] = roughness;
    roughnessImage.data[offset + 3] = 255;
  }

  colorContext.putImageData(colorImage, 0, 0);
  bumpContext.putImageData(bumpImage, 0, 0);
  roughnessContext.putImageData(roughnessImage, 0, 0);

  const colorGradient = colorContext.createLinearGradient(0, 0, 1024, 0);
  colorGradient.addColorStop(0, "rgba(10, 12, 13, 0.28)");
  colorGradient.addColorStop(0.12, "rgba(62, 66, 67, 0.06)");
  colorGradient.addColorStop(0.28, "rgba(10, 11, 12, 0.11)");
  colorGradient.addColorStop(0.5, "rgba(82, 78, 67, 0.035)");
  colorGradient.addColorStop(0.72, "rgba(10, 11, 12, 0.11)");
  colorGradient.addColorStop(0.88, "rgba(62, 66, 67, 0.06)");
  colorGradient.addColorStop(1, "rgba(10, 12, 13, 0.28)");
  colorContext.fillStyle = colorGradient;
  colorContext.fillRect(0, 0, 1024, 1024);

  colorContext.lineCap = "round";
  colorContext.lineJoin = "round";
  for (const segment of createSuntraceAsphaltRepairProfile()) {
    colorContext.strokeStyle = `rgba(${String(SUNTRACE_ASPHALT_REPAIR_COLOR.red)}, ${String(SUNTRACE_ASPHALT_REPAIR_COLOR.green)}, ${String(SUNTRACE_ASPHALT_REPAIR_COLOR.blue)}, ${String(segment.opacity)})`;
    colorContext.lineWidth = segment.lineWidth;
    colorContext.beginPath();
    colorContext.moveTo(segment.startX, segment.startY);
    colorContext.bezierCurveTo(
      THREE.MathUtils.lerp(segment.startX, segment.endX, 0.32),
      segment.controlY1,
      THREE.MathUtils.lerp(segment.startX, segment.endX, 0.68),
      segment.controlY2,
      segment.endX,
      segment.endY,
    );
    colorContext.stroke();
  }

  return {
    color: configureSurfaceTexture(
      colorCanvas,
      anisotropy,
      THREE.SRGBColorSpace,
      1,
      232,
    ),
    bump: configureSurfaceTexture(
      bumpCanvas,
      anisotropy,
      THREE.NoColorSpace,
      1,
      232,
    ),
    roughness: configureSurfaceTexture(
      roughnessCanvas,
      anisotropy,
      THREE.NoColorSpace,
      1,
      232,
    ),
  };
};

const createLimestoneTextures = (anisotropy: number): SurfaceTextureSet => {
  const canvas = createCanvas(512, 512);
  const context = requireContext(canvas);
  const bumpCanvas = createCanvas(512, 512);
  const bumpContext = requireContext(bumpCanvas);
  const roughnessCanvas = createCanvas(512, 512);
  const roughnessContext = requireContext(roughnessCanvas);
  const random = createRandom(0xc047a1);
  context.fillStyle = getHexStyle(SUNTRACE_SURFACE_PALETTE.limestone);
  context.fillRect(0, 0, 512, 512);
  bumpContext.fillStyle = "#adadad";
  bumpContext.fillRect(0, 0, 512, 512);
  roughnessContext.fillStyle = "#e3e3e3";
  roughnessContext.fillRect(0, 0, 512, 512);
  for (let index = 0; index < 2_400; index += 1) {
    const shade = Math.floor(151 + random() * 70);
    context.fillStyle = `rgba(${String(shade)}, ${String(shade - 5)}, ${String(shade - 14)}, ${String(0.035 + random() * 0.12)})`;
    const size = 1 + random() * 3;
    context.fillRect(random() * 512, random() * 512, size * 2.5, size);
    const bumpShade = Math.floor(104 + random() * 95);
    bumpContext.fillStyle = `rgb(${String(bumpShade)}, ${String(bumpShade)}, ${String(bumpShade)})`;
    bumpContext.fillRect(random() * 512, random() * 512, size * 2.2, size);
  }
  for (const stain of createSuntraceLimestoneWeatheringProfile()) {
    const drawStain = (
      drawingContext: CanvasRenderingContext2D,
      centerColor: string,
      edgeColor: string,
    ): void => {
      drawingContext.save();
      drawingContext.translate(stain.x, stain.y);
      drawingContext.rotate(stain.rotation);
      const gradient = drawingContext.createRadialGradient(
        0,
        0,
        0,
        0,
        0,
        Math.max(stain.width, stain.height) * 0.52,
      );
      gradient.addColorStop(0, centerColor);
      gradient.addColorStop(1, edgeColor);
      drawingContext.fillStyle = gradient;
      drawingContext.beginPath();
      drawingContext.ellipse(
        0,
        0,
        stain.width * 0.5,
        stain.height * 0.5,
        0,
        0,
        Math.PI * 2,
      );
      drawingContext.fill();
      drawingContext.restore();
    };
    drawStain(
      context,
      `rgba(79, 86, 84, ${String(stain.opacity)})`,
      "rgba(79, 86, 84, 0)",
    );
    drawStain(
      bumpContext,
      `rgba(104, 104, 104, ${String(stain.opacity * 0.72)})`,
      "rgba(104, 104, 104, 0)",
    );
    drawStain(
      roughnessContext,
      `rgba(188, 188, 188, ${String(stain.opacity * 0.6)})`,
      "rgba(188, 188, 188, 0)",
    );
  }
  context.strokeStyle = "rgba(83, 78, 69, 0.2)";
  bumpContext.strokeStyle = "#5f5f5f";
  roughnessContext.strokeStyle = "#fafafa";
  for (let y = 0; y <= 512; y += 64) {
    for (const drawingContext of [context, bumpContext, roughnessContext]) {
      drawingContext.lineWidth = drawingContext === context ? 2 : 4;
      drawingContext.beginPath();
      drawingContext.moveTo(0, y);
      drawingContext.lineTo(512, y);
      drawingContext.stroke();
    }
  }
  for (let row = 0; row < 8; row += 1) {
    const offset = row % 2 === 0 ? 0 : 48;
    for (let x = offset; x <= 512; x += 96) {
      for (const drawingContext of [context, bumpContext, roughnessContext]) {
        drawingContext.beginPath();
        drawingContext.moveTo(x, row * 64);
        drawingContext.lineTo(x, row * 64 + 64);
        drawingContext.stroke();
      }
    }
  }
  return {
    color: configureSurfaceTexture(
      canvas,
      anisotropy,
      THREE.SRGBColorSpace,
      3,
      2,
    ),
    bump: configureSurfaceTexture(
      bumpCanvas,
      anisotropy,
      THREE.NoColorSpace,
      3,
      2,
    ),
    roughness: configureSurfaceTexture(
      roughnessCanvas,
      anisotropy,
      THREE.NoColorSpace,
      3,
      2,
    ),
  };
};

export const SUNTRACE_GUARDRAIL_WEATHERING_RESPONSE = Object.freeze({
  bandCount: 18,
  maxOpacity: 0.095,
  maxHeight: 14,
});

export type SuntraceGuardrailWeatheringBand = {
  y: number;
  height: number;
  opacity: number;
  tone: "shadow" | "oxidation";
};

export const createSuntraceGuardrailWeatheringProfile = (): SuntraceGuardrailWeatheringBand[] => {
  const random = createRandom(0x3e7a11d4);
  return Array.from(
    { length: SUNTRACE_GUARDRAIL_WEATHERING_RESPONSE.bandCount },
    (_, index) => {
      const height = 3 + random() * (SUNTRACE_GUARDRAIL_WEATHERING_RESPONSE.maxHeight - 3);
      return {
        y: random() * (128 - height),
        height,
        opacity: 0.028 + random() * (SUNTRACE_GUARDRAIL_WEATHERING_RESPONSE.maxOpacity - 0.028),
        tone: index % 5 === 0 ? "oxidation" : "shadow",
      };
    },
  );
};

const createGuardrailTextures = (anisotropy: number): SurfaceTextureSet => {
  const colorCanvas = createCanvas(512, 128);
  const colorContext = requireContext(colorCanvas);
  const bumpCanvas = createCanvas(512, 128);
  const bumpContext = requireContext(bumpCanvas);
  const roughnessCanvas = createCanvas(512, 128);
  const roughnessContext = requireContext(roughnessCanvas);
  const colorGradient = colorContext.createLinearGradient(0, 0, 0, 128);
  colorGradient.addColorStop(0, "#c7cbc4");
  colorGradient.addColorStop(0.42, "#aeb5af");
  colorGradient.addColorStop(0.78, "#8b9490");
  colorGradient.addColorStop(1, "#737c79");
  colorContext.fillStyle = colorGradient;
  colorContext.fillRect(0, 0, 512, 128);
  bumpContext.fillStyle = "#a8aead";
  bumpContext.fillRect(0, 0, 512, 128);
  roughnessContext.fillStyle = "#d8d8d4";
  roughnessContext.fillRect(0, 0, 512, 128);
  for (const band of createSuntraceGuardrailWeatheringProfile()) {
    const opacity = String(band.opacity);
    const color = band.tone === "oxidation"
      ? `rgba(104, 78, 66, ${String(band.opacity * 0.48)})`
      : `rgba(38, 48, 47, ${opacity})`;
    colorContext.fillStyle = color;
    colorContext.fillRect(0, band.y, 512, band.height);
    bumpContext.fillStyle = `rgba(70, 77, 75, ${String(band.opacity * 0.58)})`;
    bumpContext.fillRect(0, band.y, 512, band.height * 0.68);
    roughnessContext.fillStyle = `rgba(105, 105, 101, ${String(band.opacity * 0.8)})`;
    roughnessContext.fillRect(0, band.y, 512, band.height);
  }
  colorContext.fillStyle = "rgba(238, 240, 232, 0.12)";
  colorContext.fillRect(0, 3, 512, 3);
  bumpContext.fillStyle = "rgba(220, 220, 216, 0.24)";
  bumpContext.fillRect(0, 3, 512, 2);
  roughnessContext.fillStyle = "rgba(245, 245, 240, 0.2)";
  roughnessContext.fillRect(0, 3, 512, 3);
  return {
    color: configureSurfaceTexture(
      colorCanvas,
      anisotropy,
      THREE.SRGBColorSpace,
      0.08,
      1,
    ),
    bump: configureSurfaceTexture(
      bumpCanvas,
      anisotropy,
      THREE.NoColorSpace,
      0.08,
      1,
    ),
    roughness: configureSurfaceTexture(
      roughnessCanvas,
      anisotropy,
      THREE.NoColorSpace,
      0.08,
      1,
    ),
  };
};

const createRockTextures = (anisotropy: number): SurfaceTextureSet => {
  const colorCanvas = createCanvas(512, 512);
  const colorContext = requireContext(colorCanvas);
  const bumpCanvas = createCanvas(512, 512);
  const bumpContext = requireContext(bumpCanvas);
  const roughnessCanvas = createCanvas(512, 512);
  const roughnessContext = requireContext(roughnessCanvas);
  const random = createRandom(0x57a7a921);
  colorContext.fillStyle = getHexStyle(SUNTRACE_SURFACE_PALETTE.terrain);
  colorContext.fillRect(0, 0, 512, 512);
  bumpContext.fillStyle = "#999999";
  bumpContext.fillRect(0, 0, 512, 512);
  roughnessContext.fillStyle = "#eeeeee";
  roughnessContext.fillRect(0, 0, 512, 512);

  for (let index = 0; index < 3_600; index += 1) {
    const x = random() * 512;
    const y = random() * 512;
    const width = 1 + random() * 7;
    const height = 0.6 + random() * 3;
    const tone = Math.floor(125 + random() * 92);
    colorContext.fillStyle = `rgba(${String(tone + 13)}, ${String(tone + 8)}, ${String(tone)}, ${String(0.035 + random() * 0.13)})`;
    colorContext.fillRect(x, y, width, height);
    const relief = Math.floor(84 + random() * 118);
    bumpContext.fillStyle = `rgb(${String(relief)}, ${String(relief)}, ${String(relief)})`;
    bumpContext.fillRect(x, y, width, height);
  }

  for (let stratum = 0; stratum < 18; stratum += 1) {
    const y = stratum * 29 + Math.sin(stratum * 1.7) * 6;
    colorContext.strokeStyle = `rgba(73, 68, 59, ${String(0.05 + random() * 0.08)})`;
    colorContext.lineWidth = 1 + random() * 3;
    colorContext.beginPath();
    colorContext.moveTo(-20, y);
    colorContext.bezierCurveTo(142, y + 7, 337, y - 8, 532, y + 3);
    colorContext.stroke();
    bumpContext.strokeStyle = "rgba(78, 78, 78, 0.45)";
    bumpContext.lineWidth = 2.5;
    bumpContext.beginPath();
    bumpContext.moveTo(-20, y);
    bumpContext.bezierCurveTo(142, y + 7, 337, y - 8, 532, y + 3);
    bumpContext.stroke();
  }

  return {
    color: configureSurfaceTexture(
      colorCanvas,
      anisotropy,
      THREE.SRGBColorSpace,
      2,
      3,
    ),
    bump: configureSurfaceTexture(
      bumpCanvas,
      anisotropy,
      THREE.NoColorSpace,
      2,
      3,
    ),
    roughness: configureSurfaceTexture(
      roughnessCanvas,
      anisotropy,
      THREE.NoColorSpace,
      2,
      3,
    ),
  };
};

const getNoiseHash = (x: number, y: number, seed: number): number => {
  let hash =
    Math.imul(x, 374_761_393) ^
    Math.imul(y, 668_265_263) ^
    Math.imul(seed, 1_442_695_041);
  hash = Math.imul(hash ^ (hash >>> 13), 1_274_126_177);
  return ((hash ^ (hash >>> 16)) >>> 0) / 4_294_967_295;
};

const getWrappedIndex = (value: number, period: number): number => {
  const wrapped = value % period;
  return wrapped < 0 ? wrapped + period : wrapped;
};

const sampleSeamlessNoise = (
  x: number,
  y: number,
  horizontalPeriod: number,
  seed: number,
): number => {
  const lowerX = Math.floor(x);
  const lowerY = Math.floor(y);
  const upperX = getWrappedIndex(lowerX + 1, horizontalPeriod);
  const wrappedX = getWrappedIndex(lowerX, horizontalPeriod);
  const horizontalFraction = x - lowerX;
  const verticalFraction = y - lowerY;
  const horizontalBlend =
    horizontalFraction * horizontalFraction * (3 - 2 * horizontalFraction);
  const verticalBlend =
    verticalFraction * verticalFraction * (3 - 2 * verticalFraction);
  const lower = THREE.MathUtils.lerp(
    getNoiseHash(wrappedX, lowerY, seed),
    getNoiseHash(upperX, lowerY, seed),
    horizontalBlend,
  );
  const upper = THREE.MathUtils.lerp(
    getNoiseHash(wrappedX, lowerY + 1, seed),
    getNoiseHash(upperX, lowerY + 1, seed),
    horizontalBlend,
  );
  return THREE.MathUtils.lerp(lower, upper, verticalBlend);
};

type CloudFieldSample = {
  density: number;
  relief: number;
  texture: number;
};

type CloudLayerProfile = {
  center: number;
  radius: number;
  presence: number;
};

type CloudLayerDefinition = {
  center: number;
  centerVariation: number;
  radius: number;
};

const absentCloudLayerProfile: CloudLayerProfile = {
  center: -2,
  radius: 0,
  presence: 0,
};

const smoothRange = (
  minimum: number,
  maximum: number,
  value: number,
): number => {
  const factor = THREE.MathUtils.clamp(
    (value - minimum) / (maximum - minimum),
    0,
    1,
  );
  return factor * factor * (3 - 2 * factor);
};

const createCloudLayerProfile = (
  horizontal: number,
  seed: number,
  definition: CloudLayerDefinition,
): CloudLayerProfile => {
  const centerMass = sampleSeamlessNoise(
    horizontal * 11,
    0.73,
    11,
    seed ^ 0x517cc1b7,
  );
  const centerDetail = sampleSeamlessNoise(
    horizontal * 31,
    1.61,
    31,
    seed ^ 0x68bc21eb,
  );
  const thickness = sampleSeamlessNoise(
    horizontal * 17,
    2.47,
    17,
    seed ^ 0x02e5be93,
  );
  const broadPresence = sampleSeamlessNoise(
    horizontal * 12,
    3.19,
    12,
    seed ^ 0x967a889b,
  );
  const localPresence = sampleSeamlessNoise(
    horizontal * 43,
    4.31,
    43,
    seed ^ 0x3c6ef372,
  );

  return {
    center:
      definition.center +
      (centerMass - 0.5) * definition.centerVariation +
      (centerDetail - 0.5) * definition.centerVariation * 0.44,
    radius:
      definition.radius * THREE.MathUtils.lerp(0.68, 1.16, thickness),
    presence: smoothRange(
      0.4,
      0.72,
      broadPresence * 0.68 + localPresence * 0.32,
    ),
  };
};

const getCloudLayerAltitude = (
  altitude: number,
  profile: CloudLayerProfile,
): number => {
  const edgeWidth = THREE.MathUtils.clamp(profile.radius * 0.34, 0.022, 0.052);
  const lowerEdge = profile.center - profile.radius;
  const upperEdge = profile.center + profile.radius;
  return (
    smoothRange(lowerEdge, lowerEdge + edgeWidth, altitude) *
    (1 - smoothRange(upperEdge - edgeWidth, upperEdge, altitude)) *
    profile.presence
  );
};

export const SUNTRACE_SKY_CLOUD_RESPONSE = Object.freeze({
  lowerOpacity: 1.12,
  middleOpacity: 0.88,
  upperOpacity: 0.64,
});

const sampleCloudField = (
  horizontal: number,
  altitude: number,
  seed: number,
  sample: CloudFieldSample,
): void => {
  const horizontalWarp =
    (sampleSeamlessNoise(horizontal * 6, altitude * 2, 6, seed ^ 0x6d2b79f5) -
      0.5) *
      0.15 +
    (sampleSeamlessNoise(horizontal * 16, altitude * 4, 16, seed ^ 0x1b873593) -
      0.5) *
      0.052;
  const verticalWarp =
    (sampleSeamlessNoise(horizontal * 8, altitude * 2, 8, seed ^ 0x85ebca6b) -
      0.5) *
      0.13 +
    (sampleSeamlessNoise(horizontal * 20, altitude * 5, 20, seed ^ 0xc2b2ae35) -
      0.5) *
      0.042;
  const warpedHorizontal = horizontal + horizontalWarp;
  const warpedAltitude = altitude + verticalWarp;
  const macroMass = sampleSeamlessNoise(
    warpedHorizontal * 12,
    warpedAltitude * 7,
    12,
    seed ^ 0x9e3779b9,
  );
  let horizontalFrequency = 24;
  let verticalFrequency = 14;
  let amplitude = 0.55;
  let amplitudeTotal = 0;
  let cloudField = 0;
  let foundation = 0;

  for (let octave = 0; octave < 5; octave += 1) {
    const signal = sampleSeamlessNoise(
      warpedHorizontal * horizontalFrequency,
      warpedAltitude * verticalFrequency,
      horizontalFrequency,
      seed + octave * 7_919,
    );
    if (octave === 0) {
      foundation = signal;
    }
    cloudField += signal * amplitude;
    amplitudeTotal += amplitude;
    horizontalFrequency *= 2;
    verticalFrequency *= 2;
    amplitude *= 0.5;
  }

  const edgeErosion = sampleSeamlessNoise(
    warpedHorizontal * 96,
    warpedAltitude * 54,
    96,
    seed ^ 0x27d4eb2d,
  );
  const microErosion = sampleSeamlessNoise(
    warpedHorizontal * 192,
    warpedAltitude * 108,
    192,
    seed ^ 0x165667b1,
  );
  const sunwardFoundation = sampleSeamlessNoise(
    (warpedHorizontal - 0.014) * 24,
    (warpedAltitude + 0.018) * 14,
    24,
    seed,
  );
  sample.density =
    cloudField / amplitudeTotal +
    (macroMass - 0.5) * 0.62 +
    (edgeErosion - 0.5) * 0.12 +
    (microErosion - 0.5) * 0.04;
  sample.relief = THREE.MathUtils.clamp(
    0.5 + (foundation - sunwardFoundation) * 3.25,
    0,
    1,
  );
  sample.texture = THREE.MathUtils.clamp(
    0.5 + (edgeErosion - 0.5) * 0.72 + (microErosion - 0.5) * 0.28,
    0,
    1,
  );
};

export const createDawnEnvironmentTexture = (): THREE.DataTexture => {
  const width = 2_048;
  const height = 1_024;
  const data = new Uint16Array(width * height * 4);
  const sunDirection = new THREE.Vector3(0.82, 0.23, -0.51).normalize();
  const horizontalSun = new THREE.Vector2(sunDirection.x, sunDirection.z).normalize();
  const environmentScale = 1.9;
  const lowerCloud: CloudFieldSample = { density: 0, relief: 0, texture: 0 };
  const middleCloud: CloudFieldSample = { density: 0, relief: 0, texture: 0 };
  const upperCloud: CloudFieldSample = { density: 0, relief: 0, texture: 0 };
  const saturate = (value: number): number => THREE.MathUtils.clamp(value, 0, 1);
  const lowerCloudProfiles = Array.from({ length: width }, (_, column) =>
    createCloudLayerProfile((column + 0.5) / width, 0x51a7, {
      center: 0.2,
      centerVariation: 0.13,
      radius: 0.135,
    }),
  );
  const middleCloudProfiles = Array.from({ length: width }, (_, column) =>
    createCloudLayerProfile((column + 0.5) / width, 0x6f31, {
      center: 0.47,
      centerVariation: 0.135,
      radius: 0.12,
    }),
  );
  const upperCloudProfiles = Array.from({ length: width }, (_, column) =>
    createCloudLayerProfile((column + 0.5) / width, 0x8e62, {
      center: 0.73,
      centerVariation: 0.125,
      radius: 0.105,
    }),
  );

  for (let row = 0; row < height; row += 1) {
    const vertical = (row + 0.5) / height;
    const theta = vertical * Math.PI;
    const directionY = -Math.cos(theta);
    const sinTheta = Math.sin(theta);
    for (let column = 0; column < width; column += 1) {
      const horizontal = (column + 0.5) / width;
      const phi = horizontal * Math.PI * 2 - Math.PI;
      const directionX = Math.cos(phi) * sinTheta;
      const directionZ = Math.sin(phi) * sinTheta;
      const skyHeight = saturate(directionY);
      const groundDepth = saturate(-directionY);
      const horizon = Math.exp(-Math.abs(directionY) * 6.4);
      const skyBlend = smoothRange(-0.1, 0.12, directionY);
      const skyRed = THREE.MathUtils.lerp(0.34, 0.045, Math.pow(skyHeight, 0.58));
      const skyGreen = THREE.MathUtils.lerp(0.39, 0.11, Math.pow(skyHeight, 0.62));
      const skyBlue = THREE.MathUtils.lerp(0.46, 0.235, Math.pow(skyHeight, 0.72));
      const groundCurve = Math.pow(groundDepth, 0.58);
      let red = THREE.MathUtils.lerp(
        THREE.MathUtils.lerp(0.31, 0.018, groundCurve),
        skyRed,
        skyBlend,
      );
      let green = THREE.MathUtils.lerp(
        THREE.MathUtils.lerp(0.335, 0.022, groundCurve),
        skyGreen,
        skyBlend,
      );
      let blue = THREE.MathUtils.lerp(
        THREE.MathUtils.lerp(0.36, 0.027, groundCurve),
        skyBlue,
        skyBlend,
      );
      const horizontalLength = Math.max(0.0001, Math.hypot(directionX, directionZ));
      const sunFacing = smoothRange(
        0.62,
        0.98,
        (directionX * horizontalSun.x + directionZ * horizontalSun.y) /
          horizontalLength,
      );
      const horizonWarmth = Math.pow(sunFacing, 1.15);
      const solarAtmosphere =
        horizonWarmth * Math.exp(-skyHeight * 2.85) * skyBlend;
      const coolHorizon = (1 - horizonWarmth) * horizon * skyBlend;
      red += solarAtmosphere * 0.31 + coolHorizon * 0.004;
      green += solarAtmosphere * 0.13 + coolHorizon * 0.01;
      blue += solarAtmosphere * 0.018 + coolHorizon * 0.018;
      const groundHaze = horizon * (1 - skyBlend);
      red += groundHaze * (0.025 + horizonWarmth * 0.12);
      green += groundHaze * (0.03 + horizonWarmth * 0.052);
      blue += groundHaze * (0.038 + horizonWarmth * 0.012);

      const sunDot =
        directionX * sunDirection.x +
        directionY * sunDirection.y +
        directionZ * sunDirection.z;
      const solarVeil = Math.exp((sunDot - 1) * 22);
      const solarNeighborhood =
        Math.exp((sunDot - 1) * 12) * Math.exp(-skyHeight * 0.85) * skyBlend;
      red += solarNeighborhood * 0.17;
      green += solarNeighborhood * 0.072;
      blue += solarNeighborhood * 0.018;
      const lowerCloudAltitude = getCloudLayerAltitude(
        directionY,
        lowerCloudProfiles[column] ?? absentCloudLayerProfile,
      );
      const middleCloudAltitude = getCloudLayerAltitude(
        directionY,
        middleCloudProfiles[column] ?? absentCloudLayerProfile,
      );
      const upperCloudAltitude = getCloudLayerAltitude(
        directionY,
        upperCloudProfiles[column] ?? absentCloudLayerProfile,
      );
      lowerCloud.density = 0;
      lowerCloud.relief = 0.5;
      lowerCloud.texture = 0.5;
      middleCloud.density = 0;
      middleCloud.relief = 0.5;
      middleCloud.texture = 0.5;
      upperCloud.density = 0;
      upperCloud.relief = 0.5;
      upperCloud.texture = 0.5;
      if (lowerCloudAltitude > 0) {
        sampleCloudField(horizontal, skyHeight, 0x51a7, lowerCloud);
      }
      if (middleCloudAltitude > 0) {
        sampleCloudField(
          horizontal + 0.213,
          skyHeight * 0.94,
          0x6f31,
          middleCloud,
        );
      }
      if (upperCloudAltitude > 0) {
        sampleCloudField(horizontal + 0.071, skyHeight * 1.08, 0x8e62, upperCloud);
      }
      const lowerCloudMass =
        smoothRange(0.58, 0.72, lowerCloud.density) * lowerCloudAltitude;
      const middleCloudMass =
        smoothRange(0.595, 0.735, middleCloud.density) * middleCloudAltitude;
      const upperCloudMass =
        smoothRange(0.61, 0.75, upperCloud.density) * upperCloudAltitude;
      const lowerCloudCore =
        smoothRange(0.69, 0.81, lowerCloud.density) * lowerCloudAltitude;
      const middleCloudCore =
        smoothRange(0.705, 0.82, middleCloud.density) * middleCloudAltitude;
      const upperCloudCore =
        smoothRange(0.72, 0.83, upperCloud.density) * upperCloudAltitude;
      const lowerCloudOpacity = saturate(
        lowerCloudMass *
          THREE.MathUtils.lerp(
            SUNTRACE_SKY_CLOUD_RESPONSE.lowerOpacity,
            0.92,
            skyHeight,
          ),
      );
      const middleCloudOpacity =
        middleCloudMass * SUNTRACE_SKY_CLOUD_RESPONSE.middleOpacity;
      const upperCloudOpacity =
        upperCloudMass * SUNTRACE_SKY_CLOUD_RESPONSE.upperOpacity;
      const cloudOpacity =
        1 -
        (1 - lowerCloudOpacity) *
          (1 - middleCloudOpacity) *
          (1 - upperCloudOpacity);
      const weightedCloudMass =
        lowerCloudMass + middleCloudMass + upperCloudMass;
      const middleCloudShare =
        weightedCloudMass > 0 ? middleCloudMass / weightedCloudMass : 0;
      const upperCloudShare =
        weightedCloudMass > 0 ? upperCloudMass / weightedCloudMass : 0;
      const cloudRelief =
        weightedCloudMass > 0
          ? (lowerCloud.relief * lowerCloudMass +
              middleCloud.relief * middleCloudMass +
              upperCloud.relief * upperCloudMass) /
            weightedCloudMass
          : 0.5;
      const cloudTexture =
        weightedCloudMass > 0
          ? (lowerCloud.texture * lowerCloudMass +
              middleCloud.texture * middleCloudMass +
              upperCloud.texture * upperCloudMass) /
            weightedCloudMass
          : 0.5;
      const cloudHeight = smoothRange(0.1, 0.78, directionY);
      const underside =
        (1 - smoothRange(0.1, 0.28, directionY)) *
        (1 - middleCloudShare - upperCloudShare);
      const shadowRed = THREE.MathUtils.lerp(0.145, 0.22, cloudHeight);
      const shadowGreen = THREE.MathUtils.lerp(0.15, 0.225, cloudHeight);
      const shadowBlue = THREE.MathUtils.lerp(0.15, 0.225, cloudHeight);
      const diffuseRed = THREE.MathUtils.lerp(0.34, 0.445, cloudHeight);
      const diffuseGreen = THREE.MathUtils.lerp(0.335, 0.44, cloudHeight);
      const diffuseBlue = THREE.MathUtils.lerp(0.325, 0.425, cloudHeight);
      const directionalLight = saturate(
        Math.pow(horizonWarmth, 1.15) *
          (0.18 + cloudRelief * 0.82) *
          (1 - cloudHeight * 0.28) +
          solarVeil * 0.5,
      );
      const cloudCore = THREE.MathUtils.clamp(
        lowerCloudCore + middleCloudCore + upperCloudCore,
        0,
        1,
      );
      const reliefLighting = saturate(
        0.2 + cloudRelief * 0.52 + cloudTexture * 0.14 + cloudCore * 0.08,
      );
      const bodyShadow = cloudCore * (1 - cloudRelief) * 0.055;
      const litRed = THREE.MathUtils.lerp(shadowRed, diffuseRed, reliefLighting);
      const litGreen = THREE.MathUtils.lerp(
        shadowGreen,
        diffuseGreen,
        reliefLighting,
      );
      const litBlue = THREE.MathUtils.lerp(shadowBlue, diffuseBlue, reliefLighting);
      const cloudLightRed = THREE.MathUtils.lerp(
        litRed - underside * 0.038 - bodyShadow,
        0.72,
        directionalLight,
      );
      const cloudLightGreen = THREE.MathUtils.lerp(
        litGreen - underside * 0.03 - bodyShadow * 0.9,
        0.545,
        directionalLight,
      );
      const cloudLightBlue = THREE.MathUtils.lerp(
        litBlue - underside * 0.014 - bodyShadow * 0.75,
        0.365,
        directionalLight,
      );
      red = THREE.MathUtils.lerp(
        red,
        cloudLightRed,
        cloudOpacity,
      );
      green = THREE.MathUtils.lerp(
        green,
        cloudLightGreen,
        cloudOpacity,
      );
      blue = THREE.MathUtils.lerp(
        blue,
        cloudLightBlue,
        cloudOpacity,
      );
      const cloudEdge =
        Math.max(0, lowerCloudMass - lowerCloudCore) +
        Math.max(0, middleCloudMass - middleCloudCore) +
        Math.max(0, upperCloudMass - upperCloudCore);
      const dawnRim =
        cloudEdge *
        Math.pow(horizonWarmth, 1.35) *
        (0.3 + cloudRelief * 0.7) *
        (0.38 + solarVeil * 1.15) *
        (0.62 + cloudHeight * 0.38);
      red += dawnRim * 0.34;
      green += dawnRim * 0.21;
      blue += dawnRim * 0.075;
      const sunVisibility = 1 - cloudOpacity * 0.78;
      const sunCorona = Math.exp((sunDot - 1) * 46);
      const sunHalo = Math.exp((sunDot - 1) * 220);
      const sunCore = Math.exp((sunDot - 1) * 2_800);
      red += sunCorona * 0.17 + sunHalo * 0.48 + sunCore * 4.7 * sunVisibility;
      green += sunCorona * 0.075 + sunHalo * 0.29 + sunCore * 3.65 * sunVisibility;
      blue += sunCorona * 0.018 + sunHalo * 0.105 + sunCore * 1.9 * sunVisibility;

      const dither =
        Math.sin(horizontal * Math.PI * 16 + row * 1.618_034) * 0.001;
      const offset = (row * width + column) * 4;
      data[offset] = THREE.DataUtils.toHalfFloat(Math.max(0, red + dither) * environmentScale);
      data[offset + 1] = THREE.DataUtils.toHalfFloat(Math.max(0, green + dither) * environmentScale);
      data[offset + 2] = THREE.DataUtils.toHalfFloat(Math.max(0, blue + dither) * environmentScale);
      data[offset + 3] = THREE.DataUtils.toHalfFloat(1);
    }
  }

  const texture = new THREE.DataTexture(
    data,
    width,
    height,
    THREE.RGBAFormat,
    THREE.HalfFloatType,
  );
  texture.colorSpace = THREE.LinearSRGBColorSpace;
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.wrapS = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
};

const ribbonVertexShader = `
  varying vec2 vUv;
  varying float vDepth;

  void main() {
    vUv = uv;
    vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
    vDepth = -viewPosition.z;
    gl_Position = projectionMatrix * viewPosition;
  }
`;

const ribbonFragmentShader = `
  uniform float uTime;
  uniform float uCharge;
  uniform vec3 uColor;
  varying vec2 vUv;
  varying float vDepth;

  void main() {
    float speed = mix(0.14, 1.65, uCharge);
    float pulse = pow(max(0.0, sin((vUv.x * 46.0 - uTime * speed) * 6.28318)), 9.0);
    float core = 0.08 + uCharge * 0.2;
    float fade = smoothstep(850.0, 80.0, vDepth);
    vec3 color = uColor * (core + pulse * (0.32 + uCharge * 0.42));
    gl_FragColor = vec4(color, (0.12 + uCharge * 0.12 + pulse * 0.18) * fade);
  }
`;

export const SUNTRACE_WATER_WAVES = [
  { direction: [0.94, 0.32], amplitude: 0.42, wavelength: 150, steepness: 0.45 },
  { direction: [-0.42, 0.91], amplitude: 0.25, wavelength: 96, steepness: 0.42 },
  { direction: [0.78, -0.52], amplitude: 0.14, wavelength: 62, steepness: 0.36 },
  { direction: [-0.35, -0.78], amplitude: 0.08, wavelength: 42, steepness: 0.31 },
  { direction: [0.55, 0.62], amplitude: 0.045, wavelength: 31, steepness: 0.25 },
] as const;

const waterWaveDeclarations = SUNTRACE_WATER_WAVES.map((wave, index) => {
  const suffix = index.toString();
  const directionLength = Math.hypot(...wave.direction);
  const directionX = (wave.direction[0] / directionLength).toFixed(6);
  const directionY = (wave.direction[1] / directionLength).toFixed(6);
  return `
    const vec2 WAVE_DIRECTION_${suffix} = vec2(${directionX}, ${directionY});
    const float WAVE_AMPLITUDE_${suffix} = ${wave.amplitude.toFixed(6)};
    const float WAVE_LENGTH_${suffix} = ${wave.wavelength.toFixed(6)};
    const float WAVE_STEEPNESS_${suffix} = ${wave.steepness.toFixed(6)};
  `;
}).join("");

const waterDisplacementStatements = SUNTRACE_WATER_WAVES.map(
  (_, index) => {
    const suffix = index.toString();
    return `
    float waveNumber${suffix} = 6.28318530718 / WAVE_LENGTH_${suffix};
    float wavePhase${suffix} = waveNumber${suffix} * dot(WAVE_DIRECTION_${suffix}, basePosition.xz) -
      sqrt(9.81 * waveNumber${suffix}) * uTime;
    displaced.xz += WAVE_DIRECTION_${suffix} * WAVE_STEEPNESS_${suffix} *
      WAVE_AMPLITUDE_${suffix} * cos(wavePhase${suffix});
    displaced.y += WAVE_AMPLITUDE_${suffix} * sin(wavePhase${suffix});
  `;
  },
).join("");

const waterNormalStatements = SUNTRACE_WATER_WAVES.map(
  (_, index) => {
    const suffix = index.toString();
    return `
    float waveNumber${suffix} = 6.28318530718 / WAVE_LENGTH_${suffix};
    float wavePhase${suffix} = waveNumber${suffix} * dot(WAVE_DIRECTION_${suffix}, surfacePosition) -
      sqrt(9.81 * waveNumber${suffix}) * uTime;
    float waveSine${suffix} = sin(wavePhase${suffix});
    gradient += WAVE_DIRECTION_${suffix} * waveNumber${suffix} *
      WAVE_AMPLITUDE_${suffix} * cos(wavePhase${suffix});
    verticalCorrection += WAVE_STEEPNESS_${suffix} * waveNumber${suffix} *
      WAVE_AMPLITUDE_${suffix} * waveSine${suffix};
    crestAccumulator += max(waveSine${suffix}, 0.0) * WAVE_AMPLITUDE_${suffix};
    crestWeight += WAVE_AMPLITUDE_${suffix};
  `;
  },
).join("");

const waterVertexShader = `
  uniform float uTime;
  varying vec3 vWorldPosition;
  varying vec2 vSurfacePosition;
  ${waterWaveDeclarations}

  vec3 evaluateDisplacedPosition(vec3 basePosition) {
    vec3 displaced = basePosition;
    ${waterDisplacementStatements}
    return displaced;
  }

  void main() {
    vSurfacePosition = position.xz;
    vec3 transformed = evaluateDisplacedPosition(position);
    vec4 worldPosition = modelMatrix * vec4(transformed, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

const waterFragmentShader = `
  uniform float uTime;
  uniform vec3 uSunDirection;
  uniform vec3 uFogColor;
  uniform vec3 uDeepWaterColor;
  uniform vec3 uSurfaceWaterColor;
  uniform vec3 uSkyReflectionColor;
  uniform vec3 uSunColor;
  uniform vec3 uAbsorption;
  uniform float uSunIntensity;
  uniform float uSkyReflectionIntensity;
  uniform float uFallbackOpticalDepth;
  uniform float uFresnelBias;
  uniform float uFogNear;
  uniform float uFogFar;
  varying vec3 vWorldPosition;
  varying vec2 vSurfacePosition;
  ${waterWaveDeclarations}

  vec4 evaluateNormalAndCrest(vec2 surfacePosition) {
    vec2 gradient = vec2(0.0);
    float verticalCorrection = 0.0;
    float crestAccumulator = 0.0;
    float crestWeight = 0.0;
    ${waterNormalStatements}
    vec3 normal = normalize(vec3(
      -gradient.x,
      1.0 - verticalCorrection,
      -gradient.y
    ));
    return vec4(normal, crestAccumulator / max(crestWeight, 0.0001));
  }

  vec2 evaluateFilteredDetailSlope(vec2 surfacePosition) {
    vec2 directionA = normalize(vec2(0.87, -0.49));
    vec2 directionB = normalize(vec2(-0.31, 0.95));
    vec2 directionC = normalize(vec2(0.58, 0.81));
    float phaseA = dot(surfacePosition, directionA) * 0.26 - uTime * 1.38;
    float phaseB = dot(surfacePosition, directionB) * 0.55 + uTime * 1.86;
    float phaseC = dot(surfacePosition, directionC) * 1.12 - uTime * 2.42;
    float filterA = 1.0 - smoothstep(0.35, 1.6, fwidth(phaseA));
    float filterB = 1.0 - smoothstep(0.35, 1.6, fwidth(phaseB));
    float filterC = 1.0 - smoothstep(0.35, 1.6, fwidth(phaseC));
    return directionA * cos(phaseA) * 0.045 * filterA +
      directionB * cos(phaseB) * 0.026 * filterB +
      directionC * cos(phaseC) * 0.014 * filterC;
  }

  vec3 evaluateSkyReflection(vec3 reflectedDirection) {
    float elevation = clamp(reflectedDirection.y * 0.5 + 0.5, 0.0, 1.0);
    vec3 horizon = mix(uFogColor, uSkyReflectionColor, 0.38);
    vec3 zenith = uSkyReflectionColor * 0.72;
    return mix(horizon, zenith, smoothstep(0.18, 0.92, elevation));
  }

  void main() {
    vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
    vec4 normalAndCrest = evaluateNormalAndCrest(vSurfacePosition);
    vec2 detailSlope = evaluateFilteredDetailSlope(vSurfacePosition);
    vec3 normal = normalize(normalAndCrest.xyz + vec3(
      -detailSlope.x,
      0.0,
      -detailSlope.y
    ));
    normal = gl_FrontFacing ? normal : -normal;
    float eta = gl_FrontFacing ? 1.0 / 1.333 : 1.333;
    float cosineToViewer = clamp(dot(viewDirection, normal), 0.0, 1.0);
    float dielectricF0 = pow((1.0 - 1.333) / (1.0 + 1.333), 2.0);
    float fresnelF0 = clamp(dielectricF0 + uFresnelBias, 0.0, 0.12);
    float fresnel = fresnelF0 + (1.0 - fresnelF0) *
      pow(1.0 - cosineToViewer, 5.0);
    vec3 reflectedDirection = reflect(-viewDirection, normal);
    vec3 reflectedSky = evaluateSkyReflection(reflectedDirection);
    vec3 refractedDirection = refract(-viewDirection, normal, eta);
    float opticalPathLength = clamp(
      uFallbackOpticalDepth / max(abs(refractedDirection.y), 0.26),
      uFallbackOpticalDepth,
      uFallbackOpticalDepth * 3.5
    );
    vec3 transmittance = exp(-uAbsorption * opticalPathLength);
    vec3 color = mix(
      uDeepWaterColor,
      uSurfaceWaterColor,
      transmittance * 0.72
    );
    float reflectionAmount = clamp(
      fresnel * uSkyReflectionIntensity,
      0.0,
      0.94
    );
    color = mix(color, reflectedSky, reflectionAmount);
    float sunReflection = max(
      dot(reflect(-normalize(uSunDirection), normal), viewDirection),
      0.0
    );
    float sunCore = pow(sunReflection, 170.0);
    float sunTrail = pow(sunReflection, 38.0);
    color += uSunColor * uSunIntensity * (sunCore * 0.22 + sunTrail * 0.035);
    float slopeEnergy = length(normal.xz);
    float foamBreakup = 0.5 + 0.5 * sin(
      dot(vSurfacePosition, vec2(0.091, -0.067)) +
      sin(dot(vSurfacePosition, vec2(-0.037, 0.082))) * 1.4
    );
    float crestFoam = smoothstep(0.52, 0.84, normalAndCrest.w) *
      smoothstep(0.1, 0.34, slopeEnergy) *
      smoothstep(0.28, 0.76, foamBreakup);
    color = mix(color, vec3(0.62, 0.75, 0.76), crestFoam * 0.2);
    float distanceToCamera = length(cameraPosition - vWorldPosition);
    float horizonHaze = smoothstep(uFogNear, uFogFar, distanceToCamera);
    color = mix(color, uFogColor, horizonHaze * 0.88);
    gl_FragColor = vec4(color, 1.0);
  }
`;

export const createSuntraceWaterMaterial = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uSunDirection: { value: new THREE.Vector3(0.82, 0.23, -0.51).normalize() },
      uFogColor: { value: new THREE.Color(0x72868c) },
      uDeepWaterColor: {
        value: new THREE.Color(SUNTRACE_SURFACE_PALETTE.waterDeep),
      },
      uSurfaceWaterColor: {
        value: new THREE.Color(SUNTRACE_SURFACE_PALETTE.waterSurface),
      },
      uSkyReflectionColor: {
        value: new THREE.Color(SUNTRACE_SURFACE_PALETTE.waterReflection),
      },
      uSunColor: { value: new THREE.Color(0xffc98f) },
      uAbsorption: { value: new THREE.Vector3(0.18, 0.065, 0.025) },
      uSunIntensity: { value: 2.52 },
      uSkyReflectionIntensity: { value: 0.568 },
      uFallbackOpticalDepth: { value: 5.5 },
      uFresnelBias: { value: 0.018 },
      uFogNear: { value: 360 },
      uFogFar: { value: 1_200 },
    },
    vertexShader: waterVertexShader,
    fragmentShader: waterFragmentShader,
    transparent: false,
    depthWrite: true,
    side: THREE.FrontSide,
    toneMapped: true,
  });

export const synchronizeSuntraceWaterGrade = (
  material: THREE.ShaderMaterial,
  colorGrade: SuntraceColorGrade,
): void => {
  const fogColor = material.uniforms.uFogColor?.value as THREE.Color | undefined;
  const skyReflectionColor = material.uniforms.uSkyReflectionColor?.value as
    | THREE.Color
    | undefined;
  const sunColor = material.uniforms.uSunColor?.value as THREE.Color | undefined;
  const sunIntensity = material.uniforms.uSunIntensity;
  const skyReflectionIntensity = material.uniforms.uSkyReflectionIntensity;
  const fogNear = material.uniforms.uFogNear;
  const fogFar = material.uniforms.uFogFar;

  fogColor?.copy(colorGrade.fogColor);
  skyReflectionColor?.copy(colorGrade.hemisphereSkyColor);
  sunColor?.copy(colorGrade.sunColor);
  if (sunIntensity !== undefined) {
    sunIntensity.value = colorGrade.sunIntensity;
  }
  if (skyReflectionIntensity !== undefined) {
    skyReflectionIntensity.value =
      colorGrade.environmentIntensity * 0.72 +
      colorGrade.hemisphereIntensity * 0.28;
  }
  if (fogNear !== undefined) {
    fogNear.value = colorGrade.fogNear;
  }
  if (fogFar !== undefined) {
    fogFar.value = colorGrade.fogFar;
  }
};

const getPuddleBoundaryRadius = (angle: number): number =>
  THREE.MathUtils.clamp(
    0.78 +
      Math.sin(angle * 3 + 0.42) * 0.14 +
      Math.sin(angle * 5 - 1.1) * 0.08 +
      Math.sin(angle * 8 + 0.7) * 0.035,
    0.54,
    0.98,
  );

export const sampleRoadPuddleAlpha = (u: number, v: number): number => {
  const normalizedX = (u - 0.5) * 2 / 0.64;
  const normalizedZ = (v - 0.5) * 2;
  const radius = Math.hypot(normalizedX, normalizedZ);
  const boundary = getPuddleBoundaryRadius(
    Math.atan2(normalizedZ, normalizedX),
  );
  const fade = THREE.MathUtils.clamp(
    (radius - boundary * 0.58) / (boundary * 0.42),
    0,
    1,
  );
  return 1 - fade * fade * (3 - 2 * fade);
};

const createRoadPuddleAlphaTexture = (anisotropy: number): THREE.CanvasTexture => {
  const size = 128;
  const canvas = createCanvas(size, size);
  const context = requireContext(canvas);
  const image = context.createImageData(size, size);

  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column < size; column += 1) {
      const alpha = Math.round(
        sampleRoadPuddleAlpha(
          (column + 0.5) / size,
          (row + 0.5) / size,
        ) * 255,
      );
      const offset = (row * size + column) * 4;
      image.data[offset] = alpha;
      image.data[offset + 1] = alpha;
      image.data[offset + 2] = alpha;
      image.data[offset + 3] = 255;
    }
  }

  context.putImageData(image, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.anisotropy = anisotropy;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  return texture;
};

export type SuntraceMaterialLibrary = {
  asphalt: THREE.MeshPhysicalMaterial;
  shoulder: THREE.MeshStandardMaterial;
  limestone: THREE.MeshStandardMaterial;
  limestoneDark: THREE.MeshStandardMaterial;
  terrain: THREE.MeshStandardMaterial;
  safety: THREE.MeshStandardMaterial;
  roadMarking: THREE.MeshStandardMaterial;
  puddle: THREE.MeshPhysicalMaterial;
  guardrail: THREE.MeshPhysicalMaterial;
  guardrailInner: THREE.MeshPhysicalMaterial;
  rock: THREE.MeshStandardMaterial;
  vegetation: THREE.MeshStandardMaterial;
  ribbon: THREE.ShaderMaterial;
  water: THREE.ShaderMaterial;
  environmentTexture: THREE.DataTexture;
  update: (
    time: number,
    charge: number,
    colorGrade: SuntraceColorGrade,
  ) => void;
  dispose: () => void;
};

export const SUNTRACE_SURFACE_RESPONSE = {
  asphalt: {
    roughness: 0.88,
    clearcoat: 0.025,
    clearcoatRoughness: 0.72,
    envMapIntensity: 0.32,
  },
  shoulder: { roughness: 0.97, envMapIntensity: 0.1 },
  limestone: { roughness: 0.88, envMapIntensity: 0.34 },
  limestoneDark: { roughness: 0.95, envMapIntensity: 0.14 },
  terrain: { roughness: 0.98, envMapIntensity: 0.12 },
  safety: { roughness: 0.62, metalness: 0, envMapIntensity: 0.34 },
  roadMarking: { roughness: 0.78, envMapIntensity: 0.2 },
  puddle: {
    roughness: 0.14,
    clearcoat: 0.84,
    clearcoatRoughness: 0.15,
    opacity: 0.11,
    envMapIntensity: 0.9,
  },
  guardrail: {
    roughness: 0.4,
    metalness: 0.68,
    clearcoat: 0.015,
    clearcoatRoughness: 0.76,
    envMapIntensity: 0.82,
  },
  guardrailInner: {
    roughness: 0.7,
    metalness: 0.38,
    envMapIntensity: 0.34,
  },
  rock: { roughness: 0.91, envMapIntensity: 0.2 },
  vegetation: { roughness: 0.98, envMapIntensity: 0.08 },
} as const;

export const SUNTRACE_VEGETATION_RESPONSE = Object.freeze({
  flatShading: false,
});

export const createSuntraceMaterialLibrary = (
  renderer: THREE.WebGLRenderer,
): SuntraceMaterialLibrary => {
  const anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 8);
  const asphaltTextures = createAsphaltTextures(anisotropy);
  const limestoneTextures = createLimestoneTextures(anisotropy);
  const guardrailTextures = createGuardrailTextures(anisotropy);
  const rockTextures = createRockTextures(anisotropy);
  const puddleAlphaTexture = createRoadPuddleAlphaTexture(anisotropy);
  const environmentTexture = createDawnEnvironmentTexture();
  const asphalt = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    map: asphaltTextures.color,
    bumpMap: asphaltTextures.bump,
    bumpScale: 0.035,
    roughnessMap: asphaltTextures.roughness,
    roughness: SUNTRACE_SURFACE_RESPONSE.asphalt.roughness,
    metalness: 0,
    clearcoat: SUNTRACE_SURFACE_RESPONSE.asphalt.clearcoat,
    clearcoatRoughness:
      SUNTRACE_SURFACE_RESPONSE.asphalt.clearcoatRoughness,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.asphalt.envMapIntensity,
  });
  const shoulder = new THREE.MeshStandardMaterial({
    color: SUNTRACE_SURFACE_PALETTE.shoulder,
    roughness: SUNTRACE_SURFACE_RESPONSE.shoulder.roughness,
    metalness: 0,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.shoulder.envMapIntensity,
  });
  const limestone = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    map: limestoneTextures.color,
    bumpMap: limestoneTextures.bump,
    bumpScale: 0.11,
    roughnessMap: limestoneTextures.roughness,
    roughness: SUNTRACE_SURFACE_RESPONSE.limestone.roughness,
    metalness: 0,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.limestone.envMapIntensity,
  });
  const limestoneDark = new THREE.MeshStandardMaterial({
    color: SUNTRACE_SURFACE_TINT.limestoneDark,
    map: limestoneTextures.color,
    bumpMap: limestoneTextures.bump,
    bumpScale: 0.14,
    roughnessMap: limestoneTextures.roughness,
    roughness: SUNTRACE_SURFACE_RESPONSE.limestoneDark.roughness,
    metalness: 0,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.limestoneDark.envMapIntensity,
  });
  const terrain = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    map: rockTextures.color,
    bumpMap: rockTextures.bump,
    bumpScale: 0.32,
    roughnessMap: rockTextures.roughness,
    roughness: SUNTRACE_SURFACE_RESPONSE.terrain.roughness,
    metalness: 0,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.terrain.envMapIntensity,
  });
  const safety = new THREE.MeshStandardMaterial({
    color: SUNTRACE_SURFACE_PALETTE.safety,
    roughness: SUNTRACE_SURFACE_RESPONSE.safety.roughness,
    metalness: SUNTRACE_SURFACE_RESPONSE.safety.metalness,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.safety.envMapIntensity,
    emissive: 0x000000,
    emissiveIntensity: 0,
  });
  const roadMarking = new THREE.MeshStandardMaterial({
    color: SUNTRACE_SURFACE_PALETTE.roadMarking,
    roughness: SUNTRACE_SURFACE_RESPONSE.roadMarking.roughness,
    metalness: 0,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.roadMarking.envMapIntensity,
    emissive: 0x000000,
    emissiveIntensity: 0,
  });
  const puddle = new THREE.MeshPhysicalMaterial({
    color: SUNTRACE_SURFACE_PALETTE.puddle,
    roughness: SUNTRACE_SURFACE_RESPONSE.puddle.roughness,
    metalness: 0,
    clearcoat: SUNTRACE_SURFACE_RESPONSE.puddle.clearcoat,
    clearcoatRoughness:
      SUNTRACE_SURFACE_RESPONSE.puddle.clearcoatRoughness,
    transparent: true,
    opacity: SUNTRACE_SURFACE_RESPONSE.puddle.opacity,
    alphaMap: puddleAlphaTexture,
    depthWrite: false,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.puddle.envMapIntensity,
  });
  const guardrail = new THREE.MeshPhysicalMaterial({
    color: SUNTRACE_SURFACE_PALETTE.guardrail,
    map: guardrailTextures.color,
    bumpMap: guardrailTextures.bump,
    bumpScale: 0.018,
    roughnessMap: guardrailTextures.roughness,
    roughness: SUNTRACE_SURFACE_RESPONSE.guardrail.roughness,
    metalness: SUNTRACE_SURFACE_RESPONSE.guardrail.metalness,
    clearcoat: SUNTRACE_SURFACE_RESPONSE.guardrail.clearcoat,
    clearcoatRoughness:
      SUNTRACE_SURFACE_RESPONSE.guardrail.clearcoatRoughness,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.guardrail.envMapIntensity,
    side: THREE.DoubleSide,
  });
  const guardrailInner = new THREE.MeshPhysicalMaterial({
    color: SUNTRACE_SURFACE_PALETTE.guardrailInner,
    roughness: SUNTRACE_SURFACE_RESPONSE.guardrailInner.roughness,
    metalness: SUNTRACE_SURFACE_RESPONSE.guardrailInner.metalness,
    clearcoat: 0.02,
    clearcoatRoughness: 0.82,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.guardrailInner.envMapIntensity,
    side: THREE.DoubleSide,
  });
  const rock = new THREE.MeshStandardMaterial({
    color: SUNTRACE_SURFACE_TINT.rock,
    map: rockTextures.color,
    bumpMap: rockTextures.bump,
    bumpScale: 0.38,
    roughnessMap: rockTextures.roughness,
    roughness: SUNTRACE_SURFACE_RESPONSE.rock.roughness,
    metalness: 0,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.rock.envMapIntensity,
    flatShading: false,
    vertexColors: true,
  });
  const vegetation = new THREE.MeshStandardMaterial({
    color: SUNTRACE_SURFACE_PALETTE.vegetation,
    roughness: SUNTRACE_SURFACE_RESPONSE.vegetation.roughness,
    metalness: 0,
    envMapIntensity: SUNTRACE_SURFACE_RESPONSE.vegetation.envMapIntensity,
    flatShading: SUNTRACE_VEGETATION_RESPONSE.flatShading,
  });
  const ribbon = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uCharge: { value: 0 },
      uColor: { value: new THREE.Color(0xb74732) },
    },
    vertexShader: ribbonVertexShader,
    fragmentShader: ribbonFragmentShader,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: true,
  });
  const water = createSuntraceWaterMaterial();

  const materials = [
    asphalt,
    shoulder,
    limestone,
    limestoneDark,
    terrain,
    safety,
    roadMarking,
    puddle,
    guardrail,
    guardrailInner,
    rock,
    vegetation,
    ribbon,
    water,
  ];

  return {
    asphalt,
    shoulder,
    limestone,
    limestoneDark,
    terrain,
    safety,
    roadMarking,
    puddle,
    guardrail,
    guardrailInner,
    rock,
    vegetation,
    ribbon,
    water,
    environmentTexture,
    update(time, charge, colorGrade) {
      const ribbonTime = ribbon.uniforms.uTime;
      const ribbonCharge = ribbon.uniforms.uCharge;
      const waterTime = water.uniforms.uTime;
      if (ribbonTime !== undefined) {
        ribbonTime.value = time;
      }
      if (ribbonCharge !== undefined) {
        ribbonCharge.value = THREE.MathUtils.clamp(charge, 0, 1);
      }
      if (waterTime !== undefined) {
        waterTime.value = time;
      }
      synchronizeSuntraceWaterGrade(water, colorGrade);
    },
    dispose() {
      for (const material of materials) {
        material.dispose();
      }
      for (const texture of Object.values(asphaltTextures)) {
        texture.dispose();
      }
      for (const texture of Object.values(limestoneTextures)) {
        texture.dispose();
      }
      for (const texture of Object.values(guardrailTextures)) {
        texture.dispose();
      }
      for (const texture of Object.values(rockTextures)) {
        texture.dispose();
      }
      puddleAlphaTexture.dispose();
      environmentTexture.dispose();
    },
  };
};
